/* eslint-disable no-restricted-globals -- The one sanctioned access point; see below. */
import { app } from '@ircf/config';

/**
 * The only place in the application that touches browser storage.
 *
 * `no-restricted-globals` blocks `localStorage` and `sessionStorage` everywhere
 * else, and the disable above is deliberately the single exception. The rule
 * exists because browser storage is readable by any script on the origin and
 * survives longer than people expect, so what may be written needs to be a
 * decision someone reviewed once rather than a decision made ad hoc in a
 * component.
 *
 * WHAT MAY BE STORED
 *
 * Recoverable, non-sensitive interface state the user supplied themselves and
 * would be annoyed to lose: an animation opt-out, an in-progress public enquiry
 * form. Session-scoped, so it is gone when the tab closes.
 *
 * WHAT MAY NEVER BE STORED
 *
 * Anything authenticating (the session is an HTTP-only cookie the SPA cannot
 * read, and it must stay that way), and anything from an authenticated response:
 * health answers, screening results, messages, check-ins, plans, measurements,
 * progress photographs, another person's details. A device is frequently shared;
 * a coaching record must not outlive the page that displayed it.
 *
 * Every operation is failure-tolerant. Storage throws when it is full, and is
 * absent entirely in private modes and locked-down browsers. A form that breaks
 * because a draft could not be saved is worse than a form that silently forgets.
 */

function namespaced(key: string): string {
  return `${app.slug}:${key}`;
}

/** Returns the store, or `null` when it is unavailable or access throws. */
function store(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    // Safari throws on access rather than returning undefined when cookies and
    // site data are blocked.
    return null;
  }
}

export const sessionStore = {
  read(key: string): string | null {
    try {
      return store()?.getItem(namespaced(key)) ?? null;
    } catch {
      return null;
    }
  },

  write(key: string, value: string): void {
    try {
      store()?.setItem(namespaced(key), value);
    } catch {
      // Full, or disabled. Not worth surfacing to the user.
    }
  },

  remove(key: string): void {
    try {
      store()?.removeItem(namespaced(key));
    } catch {
      // As above.
    }
  },

  /**
   * Reads and parses JSON, returning `null` on anything unexpected.
   *
   * The caller supplies the validator, because stored JSON is untrusted input:
   * it may have been written by an older build with a different shape, or edited
   * by hand. A draft that fails validation is discarded rather than repaired.
   */
  readJson<T>(key: string, validate: (value: unknown) => T | null): T | null {
    const raw = this.read(key);
    if (raw === null) return null;
    try {
      return validate(JSON.parse(raw));
    } catch {
      return null;
    }
  },

  writeJson(key: string, value: unknown): void {
    try {
      this.write(key, JSON.stringify(value));
    } catch {
      // Circular or non-serialisable. A caller bug, but not one worth a crash.
    }
  },
} as const;
